import React, { useMemo, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSettings } from '../context/SettingsContext';
import { useDevice } from '../hooks/useDevice';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import client from '../api/client';
import {
    Wallet as WalletIcon,
    TrendingUp,
    TrendingDown,
    AlertCircle,
    Clock,
    CheckSquare,
    DollarSign,
    Calendar,
    Plus,
    Coins,
    QrCode,
    Ticket,
    ChevronRight,
    Zap,
    LayoutDashboard,
    Package,
    Users as UsersIcon,
    FileText
} from 'lucide-react';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer
} from 'recharts';

// Components
import TransactionModal from '../components/TransactionModal';
import LeadModal from '../components/LeadModal';
import TaskModal from '../components/TaskModal';
import BrowserQrScanner from '../components/BrowserQrScanner';

const MetricCard = ({ title, value, icon: Icon, colorClass, detail, onAction, actionIcon: ActionIcon = Plus }) => (
    <div className="premium-card p-6 md:p-8 group relative overflow-hidden italic shadow-2xl transition-all hover:border-blue-500/20">
        <div className="flex justify-between items-start mb-6">
            <div className={`p-3 md:p-4 rounded-2xl md:rounded-3xl ${colorClass} border border-white/10 shadow-xl`}>
                <Icon className="w-5 h-5 md:w-6 md:h-6" />
            </div>
            {onAction && (
                <button
                    onClick={(e) => { e.stopPropagation(); onAction(); }}
                    className="p-2.5 bg-neutral-800 hover:bg-white hover:text-black text-white rounded-xl border border-neutral-700 transition-all shadow-2xl active:scale-95"
                    title="Быстрое действие"
                >
                    <ActionIcon className="w-4 h-4" />
                </button>
            )}
        </div>
        <div>
            <p className="text-neutral-500 text-[10px] font-black uppercase tracking-[0.2em] mb-2">{title}</p>
            <h3 className="text-2xl md:text-3xl font-black text-white tracking-tighter leading-none">{value}</h3>
            {detail && <p className="text-[10px] text-neutral-400 mt-3 font-bold uppercase tracking-widest opacity-60">{detail}</p>}
        </div>
        <div className={`absolute -right-10 -bottom-10 w-32 h-32 ${colorClass} opacity-[0.03] blur-3xl rounded-full pointer-events-none`}></div>
    </div>
);

const Dashboard = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { currency } = useSettings();
    const { isMobile, isTelegram } = useDevice();
    
    // States
    const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
    const [initialTransactionData, setInitialTransactionData] = useState(null);
    const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);
    const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
    
    // UI state
    const [chartMetric, setChartMetric] = useState('cashflow');
    const [financeCardIndex, setFinanceCardIndex] = useState(0); 
    const [scannedItem, setScannedItem] = useState(null);
    const [scannedParticipant, setScannedParticipant] = useState(null);
    const [scannedCode, setScannedCode] = useState(null);
    const [isBindingMode, setIsBindingMode] = useState(false);
    const [showBrowserScanner, setShowBrowserScanner] = useState(false);

    // Settings Query
    const { data: settings } = useQuery({
        queryKey: ['settings'],
        queryFn: async () => (await client.get('/settings')).data
    });

    const dateRange = useMemo(() => {
        if (!settings) return { start: null, end: null };
        const range = settings.dashboard_date_range || 'this_month';
        const now = new Date();
        let start = new Date();
        let end = new Date();

        switch (range) {
            case 'this_month':
                start = new Date(now.getFullYear(), now.getMonth(), 1);
                end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                break;
            case 'last_month':
                start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                end = new Date(now.getFullYear(), now.getMonth(), 0);
                break;
            case 'last_30_days':
                start.setDate(now.getDate() - 30);
                break;
            case 'this_year':
                start = new Date(now.getFullYear(), 0, 1);
                end = new Date(now.getFullYear(), 11, 31);
                break;
            default:
                start = new Date(now.getFullYear(), now.getMonth(), 1);
        }
        const fmt = (d) => d.toISOString().split('T')[0];
        return { start: fmt(start), end: fmt(end) };
    }, [settings]);

    const { data: dashboardData, isLoading } = useQuery({
        queryKey: ['dashboard', dateRange],
        queryFn: async () => {
            const params = {};
            if (dateRange.start) params.start_date = dateRange.start;
            if (dateRange.end) params.end_date = dateRange.end;
            return (await client.get('/dashboard', { params })).data;
        },
        enabled: !!settings
    });

    const { data: tasks } = useQuery({
        queryKey: ['tasks'],
        queryFn: async () => (await client.get('/tasks')).data
    });

    const { data: categories } = useQuery({
        queryKey: ['categories'],
        queryFn: async () => (await client.get('/categories')).data
    });

    const { data: projects } = useQuery({
        queryKey: ['projects'],
        queryFn: async () => (await client.get('/projects')).data
    });

    const { data: inventoryGroups } = useQuery({
        queryKey: ['inventory'],
        queryFn: async () => (await client.get('/inventory')).data,
        enabled: isBindingMode
    });

    const bindMutation = useMutation({
        mutationFn: ({ instanceId, identifier }) => client.patch(`/inventory/instances/${instanceId}`, { identifier }),
        onSuccess: () => {
            setIsBindingMode(false);
            setScannedCode(null);
            const msg = "✅ Код успешно привязан!";
            if (window.Telegram?.WebApp?.isVersionAtLeast?.('6.2')) {
                window.Telegram.WebApp.showAlert(msg);
            } else {
                alert(msg);
            }
        }
    });

    // NFC & Deep Link logic (Mobile only functionality, but kept clean)
    useEffect(() => {
        if (!isMobile) return;
        const startParam = window.Telegram?.WebApp?.initDataUnsafe?.start_param;
        if (startParam) {
            client.get(`/inventory/instances/lookup/${startParam}`)
                .then(res => { setScannedItem(res.data); })
                .catch(() => { setScannedCode(startParam); setIsBindingMode(true); });
        }
    }, [isMobile]);

    useEffect(() => {
        if (!isMobile || !('NDEFReader' in window)) return;
        let ndef = null;
        const startNfc = async () => {
            try {
                ndef = new NDEFReader();
                await ndef.scan();
                ndef.onreading = async (event) => {
                    const decoder = new TextDecoder();
                    for (const record of event.message.records) {
                        const text = decoder.decode(record.data);
                        let identifier = text;
                        if (text.includes('startapp=')) identifier = text.split('startapp=')[1];
                        try {
                            const res = await client.get(`/inventory/instances/lookup/${identifier}`);
                            setScannedItem(res.data);
                        } catch (err) {
                            if (err.response?.status === 404) {
                                setScannedCode(identifier);
                                setIsBindingMode(true);
                            }
                        }
                    }
                };
            } catch (e) { console.warn("NFC background error:", e); }
        };
        startNfc();
        return () => { if (ndef) ndef.onreading = null; };
    }, [isMobile]);

    const handleScanQr = () => {
        const tg = window.Telegram?.WebApp;
        if (isTelegram && tg?.isVersionAtLeast?.('6.4')) {
            tg.showScanQrPopup({ text: "Сканировать (ТМЦ или Билет)" }, async (text) => {
                const code = text?.trim();
                if (!code) return true;

                // 1. Try Ticket Check-in if matches pattern
                if (code.startsWith('MCC-')) {
                    try {
                        const res = await client.post('/events/check-in', { ticket_code: code });
                        setScannedParticipant(res.data);
                        tg.closeScanQrPopup();
                        return true;
                    } catch (err) {
                        // If 404, maybe it's an inventory item that happens to start with MCC-
                        if (err.response?.status !== 404) {
                            alert(err.response?.data?.detail || "Ошибка билета");
                            tg.closeScanQrPopup();
                            return true;
                        }
                    }
                }

                // 2. Try Inventory Lookup
                try {
                    const res = await client.get(`/inventory/instances/lookup/${code}`);
                    setScannedItem(res.data);
                    tg.closeScanQrPopup();
                    return true;
                } catch (err) {
                    if (err.response?.status === 404) {
                        setScannedCode(code);
                        setIsBindingMode(true);
                    } else {
                        alert("Ошибка поиска");
                    }
                    tg.closeScanQrPopup();
                    return true;
                }
            });
        } else { setShowBrowserScanner(true); }
    };

    const handleBrowserScan = async (text) => {
        setShowBrowserScanner(false);
        const code = text?.trim();
        if (!code) return;

        // 1. Try Ticket Check-in if matches pattern
        if (code.startsWith('MCC-')) {
            try {
                const res = await client.post('/events/check-in', { ticket_code: code });
                setScannedParticipant(res.data);
                return;
            } catch (err) {
                if (err.response?.status !== 404) {
                    alert(err.response?.data?.detail || "Ошибка билета");
                    return;
                }
            }
        }

        // 2. Try Inventory Lookup
        try {
            const res = await client.get(`/inventory/instances/lookup/${code}`);
            setScannedItem(res.data);
        } catch (err) {
            if (err.response?.status === 404) {
                setScannedCode(code);
                setIsBindingMode(true);
            } else { alert("Ошибка при поиске кода"); }
        }
    };

    const openQuickTransaction = (type = 'Расход', category = '') => {
        setInitialTransactionData({
            type, category, date: new Date().toISOString().split('T')[0], amount: '',
            wallet_id: dashboardData?.wallets?.[0]?.id || '', notes: ''
        });
        setIsTransactionModalOpen(true);
    };

    const showWidget = (widgetId) => {
        if (!settings || !settings.dashboard_widgets) return true;
        return settings.dashboard_widgets.split(',').includes(widgetId);
    };

    if (isLoading || !settings) return (
        <div className="space-y-8 p-4 md:p-8 animate-in fade-in duration-500 italic">
            <div className="flex justify-between items-end mb-8">
                <div className="h-10 w-64 bg-neutral-900 rounded-2xl animate-pulse" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[1, 2, 3, 4].map(i => <div key={i} className="h-40 bg-neutral-900 rounded-[2.5rem] border border-neutral-800 animate-pulse" />)}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 h-[450px] bg-neutral-900 rounded-[3rem] border border-neutral-800 animate-pulse" />
                <div className="h-[450px] bg-neutral-900 rounded-[3rem] border border-neutral-800 animate-pulse" />
            </div>
        </div>
    );

    const totalBalance = dashboardData?.total_balance || 0;
    const chartData = dashboardData?.chart_data || [];
    const urgentTasks = tasks?.filter(t => t.status !== "Завершена").slice(0, 6) || [];

    // --- RESPONSIVE UI ---
    return (
        <div className="max-w-7xl mx-auto space-y-8 md:space-y-12 animate-in fade-in duration-500 pb-24 md:pb-32 px-4 italic">
            {/* Unified Header */}
            <header className="flex flex-col md:flex-row md:justify-between md:items-center gap-6 pt-4 md:pt-8">
                <div className="flex items-center gap-4 md:gap-6">
                    <button onClick={handleScanQr} className="w-14 h-14 md:w-16 md:h-16 bg-blue-600 rounded-2xl md:rounded-3xl flex items-center justify-center text-white shadow-2xl shadow-blue-600/20 active:scale-95 transition-all">
                        <QrCode size={isMobile ? 24 : 32} />
                    </button>
                    <div>
                        <h1 className="text-2xl md:text-4xl font-black text-white italic tracking-tighter leading-none uppercase">
                            {isMobile ? 'Dashboard' : `Привет, ${user?.fio?.split(' ')[0] || user?.name || 'Партнер'}`}
                        </h1>
                        <p className="text-[10px] md:text-xs text-neutral-500 font-black uppercase tracking-[0.2em] md:tracking-[0.4em] mt-2 italic">
                            Million Connections ERP 3.0
                        </p>
                    </div>
                </div>
                
                {/* Profile Widget (Visible on Desktop) */}
                <div className="hidden md:flex items-center gap-4 bg-neutral-900 p-2 pr-6 rounded-full border border-neutral-800 shadow-xl">
                    {user?.photo_url ? (
                        <img src={user.photo_url.startsWith('/') ? `/api${user.photo_url}` : user.photo_url} alt="Profile" className="w-10 h-10 rounded-full object-cover border border-white/10" />
                    ) : (
                        <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-black text-xs">{(user?.fio || '??').substring(0,2)}</div>
                    )}
                    <div className="text-right">
                        <p className="text-[10px] font-black text-white uppercase tracking-wider">{user?.fio || user?.name}</p>
                        <p className="text-[8px] font-black text-blue-500 uppercase tracking-widest">{user?.role}</p>
                    </div>
                </div>

                {/* Date Reference (Mobile Alternative for Profile) */}
                {isMobile && (
                    <div className="flex justify-between items-center bg-neutral-900/50 p-4 rounded-2xl border border-neutral-800">
                        <div className="flex items-center gap-2">
                             <Calendar size={14} className="text-blue-500" />
                             <span className="text-[10px] font-black text-neutral-400 uppercase tracking-widest">
                                {settings?.dashboard_date_range === 'this_month' ? 'Этот месяц' : 'Период активен'}
                             </span>
                        </div>
                        <button onClick={() => navigate('/events')} className="text-[10px] font-black text-blue-500 uppercase flex items-center gap-1">События <ChevronRight size={10}/></button>
                    </div>
                )}
            </header>

            {/* Metrics Section - Responsive Grid */}
            {dashboardData?.can_view_finance !== false && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {showWidget('balance') && (
                        <MetricCard
                            title="Общий Баланс"
                            value={`${totalBalance.toLocaleString()} ${currency}`}
                            icon={WalletIcon}
                            colorClass="bg-blue-600 text-white"
                            detail={`${dashboardData?.wallets?.length || 0} кошельков`}
                            onAction={() => openQuickTransaction('Доход')}
                        />
                    )}
                    {showWidget('income') && (
                        <MetricCard
                            title="Доходы (период)"
                            value={`${(dashboardData?.total_income || 0).toLocaleString()} ${currency}`}
                            icon={TrendingUp}
                            colorClass="bg-emerald-600 text-white"
                            detail="Подтвержденные поступления"
                        />
                    )}
                    {showWidget('expense') && (
                        <MetricCard
                            title="Расходы (период)"
                            value={`${(dashboardData?.total_expense || 0).toLocaleString()} ${currency}`}
                            icon={TrendingDown}
                            colorClass="bg-rose-600 text-white"
                            detail="Операционные затраты"
                        />
                    )}
                    {showWidget('dividends') && (
                        <MetricCard
                            title="Дивиденды"
                            value={`${(dashboardData?.total_dividends || 0).toLocaleString()} ${currency}`}
                            icon={Coins}
                            colorClass="bg-purple-600 text-white"
                            detail="Выплаченные дивиденды"
                            onAction={() => openQuickTransaction('Расход', 'Дивиденды')}
                        />
                    )}
                </div>
            )}

            {/* Analytics & Tasks Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Main Graph (Hidden on small mobile if needed, but here we make it responsive) */}
                {showWidget('charts') && dashboardData?.can_view_finance !== false && (
                    <div className="lg:col-span-2 bg-neutral-900 border border-neutral-800 p-8 md:p-10 rounded-[3rem] shadow-2xl relative overflow-hidden italic">
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-10">
                            <div>
                                <h3 className="text-xl font-black text-white uppercase italic tracking-tighter leading-none mb-2">Финансовый Импульс</h3>
                                <p className="text-[10px] text-neutral-500 font-bold uppercase tracking-widest">Анализ движения за период</p>
                            </div>
                            <div className="flex gap-2 bg-neutral-950 p-1.5 rounded-2xl border border-neutral-800 self-stretch md:self-auto">
                                {['cashflow', 'profit', 'balance'].map(m => (
                                    <button
                                        key={m}
                                        onClick={() => setChartMetric(m)}
                                        className={`flex-1 md:flex-none px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${chartMetric === m ? 'bg-blue-600 text-white shadow-xl' : 'text-neutral-500 hover:text-neutral-300'}`}
                                    >{m}</button>
                                ))}
                            </div>
                        </div>
                        <div className="h-[300px] md:h-[350px] w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={chartData}>
                                    <defs>
                                        <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2}/>
                                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#262626" vertical={false} />
                                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: '#525252', fontSize: 10, fontWeight: 'bold' }} dy={10} hide={isMobile} />
                                    <YAxis hide />
                                    <Tooltip contentStyle={{ backgroundColor: '#171717', border: 'none', borderRadius: '16px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)' }} />
                                    <Area type="monotone" dataKey={chartMetric === 'cashflow' ? 'income' : chartMetric} stroke="#3b82f6" strokeWidth={4} fillOpacity={1} fill="url(#colorValue)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                )}

                {/* Tasks Queue - Premium layout */}
                <div className="bg-neutral-900 border border-neutral-800 p-8 md:p-10 rounded-[3rem] shadow-2xl relative overflow-hidden italic h-full">
                    <div className="flex justify-between items-center mb-8">
                         <h3 className="text-xl font-black text-white uppercase italic tracking-tighter leading-none">Очередь Задач</h3>
                         <button onClick={() => navigate('/tasks')} className="p-2 bg-neutral-950 rounded-xl text-neutral-500 hover:text-white transition-all"><Zap size={18}/></button>
                    </div>
                    <div className="space-y-4">
                        {urgentTasks.length === 0 ? (
                            <div className="py-20 text-center opacity-20"><CheckSquare size={48} className="mx-auto mb-4" /><p className="text-[10px] font-black uppercase tracking-widest">Все задачи закрыты</p></div>
                        ) : urgentTasks.slice(0, 5).map(task => (
                            <div key={task.id} className="p-5 bg-neutral-950 border border-neutral-800 rounded-3xl group hover:border-blue-500/30 transition-all cursor-pointer">
                                <div className="flex justify-between items-start mb-3">
                                    <span className={`text-[9px] font-black px-2 py-1 rounded-lg uppercase tracking-widest italic border ${task.is_hot ? 'bg-rose-500/10 text-rose-500 border-rose-500/20' : 'bg-blue-500/10 text-blue-500 border-blue-500/20'}`}>
                                        {task.status}
                                    </span>
                                    <Clock size={12} className="text-neutral-700" />
                                </div>
                                <p className="text-sm font-black text-white line-clamp-1 leading-tight mb-2">{task.title}</p>
                                <p className="text-[9px] text-neutral-500 font-black uppercase tracking-widest truncate">{task.project_name || 'Локальная задача'}</p>
                            </div>
                        ))}
                    </div>
                    <button onClick={() => setIsTaskModalOpen(true)} className="w-full mt-8 py-5 bg-white text-black rounded-[2rem] text-[10px] font-black uppercase tracking-widest shadow-xl active:scale-95 transition-all">Добавить тикет</button>
                </div>
            </div>

            {/* Quick Actions Grid (Mobile focus but useful everywhere) */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 pt-4">
                <button onClick={() => navigate('/ops')} className="bg-neutral-900 border border-neutral-800 p-6 rounded-[2.5rem] flex flex-col items-center gap-3 transition-all active:scale-95 hover:border-blue-500/30 group">
                    <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 flex items-center justify-center text-indigo-500 group-hover:bg-indigo-500 group-hover:text-white transition-all"><WalletIcon size={24}/></div>
                    <span className="text-[10px] font-black text-neutral-400 group-hover:text-white uppercase tracking-widest">Финансы</span>
                </button>
                <button onClick={() => navigate('/inventory')} className="bg-neutral-900 border border-neutral-800 p-6 rounded-[2.5rem] flex flex-col items-center gap-3 transition-all active:scale-95 hover:border-blue-500/30 group">
                    <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 flex items-center justify-center text-emerald-500 group-hover:bg-emerald-500 group-hover:text-white transition-all"><Package size={24}/></div>
                    <span className="text-[10px] font-black text-neutral-400 group-hover:text-white uppercase tracking-widest">Склад и ТМЦ</span>
                </button>
                <button onClick={() => navigate('/crm')} className="bg-neutral-900 border border-neutral-800 p-6 rounded-[2.5rem] flex flex-col items-center gap-3 transition-all active:scale-95 hover:border-blue-500/30 group">
                    <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center text-blue-500 group-hover:bg-blue-500 group-hover:text-white transition-all"><UsersIcon size={24}/></div>
                    <span className="text-[10px] font-black text-neutral-400 group-hover:text-white uppercase tracking-widest">Ленд-CRM</span>
                </button>
                <button onClick={() => navigate('/invoices')} className="bg-neutral-900 border border-neutral-800 p-6 rounded-[2.5rem] flex flex-col items-center gap-3 transition-all active:scale-95 hover:border-blue-500/30 group">
                    <div className="w-12 h-12 rounded-2xl bg-amber-500/10 flex items-center justify-center text-amber-500 group-hover:bg-amber-500 group-hover:text-white transition-all"><FileText size={24}/></div>
                    <span className="text-[10px] font-black text-neutral-400 group-hover:text-white uppercase tracking-widest">Счета</span>
                </button>
            </div>

            {/* Modals & Scanning Logic */}
            <TransactionModal 
                isOpen={isTransactionModalOpen} 
                onClose={() => setIsTransactionModalOpen(false)} 
                wallets={dashboardData?.wallets} 
                categories={categories} 
                projects={projects} 
                initialData={initialTransactionData} 
                onSuccess={() => { queryClient.invalidateQueries(['dashboard']); setIsTransactionModalOpen(false); }} 
            />
            <LeadModal isOpen={isLeadModalOpen} onClose={() => setIsLeadModalOpen(false)} onSuccess={() => queryClient.invalidateQueries(['dashboard'])} />
            <TaskModal isOpen={isTaskModalOpen} onClose={() => setIsTaskModalOpen(false)} onSuccess={() => queryClient.invalidateQueries(['tasks'])} />
            
            {showBrowserScanner && <BrowserQrScanner onScan={handleBrowserScan} onClose={() => setShowBrowserScanner(false)} title="Сканирование ТМЦ" />}

            {/* Binding Slideup for Mobile */}
            {isBindingMode && (
                <div className="fixed inset-0 z-[150] bg-black/95 backdrop-blur-3xl flex items-end animate-in fade-in slide-in-from-bottom-20 duration-500 italic">
                    <div className="w-full bg-neutral-950 border-t border-neutral-800 rounded-t-[3rem] p-8 pb-16 max-h-[85vh] flex flex-col">
                        <div className="w-12 h-1.5 bg-neutral-800 rounded-full mx-auto mb-8" />
                        <h2 className="text-2xl font-black text-white uppercase italic tracking-tighter leading-none mb-3">Привязка ТМЦ</h2>
                        <p className="text-xs text-neutral-500 font-bold mb-8">Идентификатор <code className="text-blue-500 font-black">{scannedCode}</code> не найден. Выберите объект для привязки:</p>
                        <div className="flex-1 overflow-y-auto space-y-3 pr-2 scrollbar-hide">
                            {inventoryGroups?.map(group => group.instances?.map(inst => (
                                <button key={inst.id} onClick={() => bindMutation.mutate({ instanceId: inst.id, identifier: scannedCode })} className="w-full bg-neutral-900 p-6 rounded-[2rem] flex justify-between items-center border border-neutral-800 text-left hover:border-blue-500 transition-all active:scale-[0.98]">
                                    <div>
                                        <div className="text-[10px] text-neutral-500 font-black uppercase tracking-widest mb-1">{group.name}</div>
                                        <div className="text-base font-black text-white italic">{inst.identifier || `#${inst.id}`}</div>
                                    </div>
                                    <ChevronRight size={18} className="text-neutral-700" />
                                </button>
                            )))}
                        </div>
                        <button onClick={() => setIsBindingMode(false)} className="w-full mt-8 py-5 text-neutral-500 uppercase text-[10px] font-black tracking-widest hover:text-white transition-all">Отмена</button>
                    </div>
                </div>
            )}

            {/* Scanned Item Feedback Modal */}
            {scannedItem && (
                <div className="fixed inset-0 z-[150] bg-black/95 backdrop-blur-3xl flex items-center justify-center p-4 animate-in fade-in zoom-in-95 duration-300 italic">
                    <div className="bg-neutral-900 border border-neutral-800 w-full max-w-lg rounded-[3rem] p-10 shadow-3xl text-center relative overflow-hidden">
                        <div className="absolute top-0 left-0 right-0 h-2 bg-blue-600 shadow-[0_0_20px_rgba(37,99,235,0.5)]" />
                        <div className="w-20 h-20 bg-blue-600/20 rounded-full flex items-center justify-center mx-auto mb-8 text-blue-500 border border-blue-500/30">
                            <Package size={40} />
                        </div>
                        <h3 className="text-2xl font-black text-white italic uppercase tracking-tighter mb-2 leading-none">Объект Найден</h3>
                        <p className="text-[10px] text-neutral-500 font-black uppercase tracking-widest mb-8">{scannedItem.identifier}</p>
                        
                        <div className="bg-neutral-950 p-6 rounded-3xl border border-neutral-800 text-left mb-8">
                             <p className="text-[8px] text-zinc-600 font-black uppercase tracking-widest mb-2">Технические данные</p>
                             <div className="flex justify-between items-center">
                                 <span className="text-sm font-bold text-white uppercase italic">{scannedItem.model || 'Unknown Model'}</span>
                                 <span className="text-[10px] font-black text-blue-500 bg-blue-500/10 px-3 py-1 rounded-full border border-blue-500/20">{scannedItem.status}</span>
                             </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <button onClick={() => navigate(`/inventory/${scannedItem.id}`)} className="bg-blue-600 text-white py-5 rounded-2xl font-black uppercase text-[10px] tracking-widest active:scale-95 transition-all">Подробнее</button>
                            <button onClick={() => setScannedItem(null)} className="bg-neutral-800 text-white py-5 rounded-2xl font-black uppercase text-[10px] tracking-widest active:scale-95 transition-all">Закрыть</button>
                        </div>
                    </div>
                </div>
            )}
            {/* Scanned Participant Feedback Modal */}
            {scannedParticipant && (
                <div className="fixed inset-0 z-[160] bg-black/95 backdrop-blur-3xl flex items-center justify-center p-4 animate-in fade-in zoom-in-95 duration-300 italic">
                    <div className="bg-neutral-900 border border-neutral-800 w-full max-w-xl rounded-[3rem] p-8 md:p-12 shadow-3xl relative overflow-hidden">
                        <div className={`absolute top-0 left-0 right-0 h-2 shadow-[0_0_20px_rgba(37,99,235,0.5)] ${scannedParticipant.is_regular ? 'bg-amber-500' : 'bg-blue-600'}`} />
                        
                        <div className="flex flex-col md:flex-row gap-8 items-start mb-10">
                            <div className={`w-24 h-24 rounded-[2rem] flex items-center justify-center shrink-0 border shadow-2xl ${scannedParticipant.is_regular ? 'bg-amber-500/10 text-amber-500 border-amber-500/20' : 'bg-blue-600/10 text-blue-500 border-blue-500/20'}`}>
                                <Ticket size={48} />
                            </div>
                            <div className="text-left flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                    <h3 className="text-3xl font-black text-white italic uppercase tracking-tighter leading-none">{scannedParticipant.user_name}</h3>
                                    {scannedParticipant.is_regular && (
                                        <span className="bg-amber-500 text-black text-[8px] font-black px-2 py-1 rounded-lg uppercase tracking-widest shadow-lg">Regular</span>
                                    )}
                                </div>
                                <p className="text-xs text-neutral-500 font-black uppercase tracking-widest leading-none">{scannedParticipant.phone || 'NO PHONE'}</p>
                                <div className="flex gap-2 mt-4">
                                    <span className="bg-blue-500/10 text-blue-500 text-[10px] font-black px-3 py-1.5 rounded-full border border-blue-500/20 uppercase tracking-widest">{scannedParticipant.tariff}</span>
                                    <span className="bg-neutral-800 text-neutral-400 text-[10px] font-black px-3 py-1.5 rounded-full border border-neutral-700 uppercase tracking-widest">Участий: {scannedParticipant.participation_count}</span>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-10">
                             <div className="bg-neutral-950 p-6 rounded-3xl border border-neutral-800 text-left">
                                 <p className="text-[8px] text-zinc-600 font-black uppercase tracking-widest mb-2">Ниша / Бизнес</p>
                                 <p className="text-sm font-bold text-white uppercase italic truncate">
                                    {scannedParticipant.niche || '—'}
                                 </p>
                             </div>
                             <div className="bg-neutral-950 p-6 rounded-3xl border border-neutral-800 text-left">
                                 <p className="text-[8px] text-zinc-600 font-black uppercase tracking-widest mb-2">Город</p>
                                 <p className="text-sm font-bold text-white uppercase italic truncate">
                                    {scannedParticipant.city || '—'}
                                 </p>
                             </div>
                             <div className="col-span-full bg-neutral-950 p-6 rounded-3xl border border-neutral-800 text-left">
                                 <p className="text-[8px] text-zinc-600 font-black uppercase tracking-widest mb-2">Цель участия</p>
                                 <p className="text-sm font-bold text-white italic line-clamp-2">
                                    {scannedParticipant.goal || '—'}
                                 </p>
                             </div>
                        </div>

                        <button 
                            onClick={() => setScannedParticipant(null)} 
                            className="w-full bg-white text-black py-6 rounded-[2rem] font-black uppercase text-xs tracking-[0.2em] shadow-2xl active:scale-95 transition-all"
                        >
                            Готово / Понял
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Dashboard;
